import React, { useEffect, useRef, useState } from "react";
import { Tabs } from "@components/TabsTable";
import { Input } from "@components/Input";
import Search from "@assets/icons/search";
import { Upload, X } from "lucide-react";
import styles from "./styles.module.scss";
import { ExpandableTable } from "@components/ExpandebleTable";
import { getColumns } from "./columns";
import { buildGroup, getData } from "./mock";
import ModalBox from "@components/ModalBox";
import { FileUploadDropzone } from "@components/file-upload-dropzone";
import { useFormStore } from "src/store/dataEntryForm";
import { CategoriesService } from "@services/amazon/categories/categories.service";
import classNames from "classnames";
import { Tabs as TabsType } from "./model";
import { getFormComponent, getTitle, tabs } from "./utils";
import { dataEntryApi } from "@services/data-entry/data-entry.api";
import {
  CogsDeleteQueryParams,
  CogsResponseDto,
  CogsUpdateBody,
  CogsUpdateQueryParams,
} from "@services/data-entry/data-entry.model";
import { COGSForm, COGSFormData } from "./components/SearchCOGS";
import { toast } from "react-toastify";

const amazonFeeOptions = [
  { value: "baseFulfillmentFee", label: "Base Fulfillment Fee" },
  { value: "fbaFulfillmentFees", label: "FBA Fulfillment Fees" },
  { value: "refundAdministrationFee", label: "Refund Administration Fee" },
  { value: "referralFee", label: "Referral Fee" },
  {
    value: "monthlyInventoryStorageFee",
    label: "Monthly Inventory Storage Fee",
  },
  { value: "totalAmazonFee", label: "Total Amazon Fee" },
];

const unitsReturnedOptions = [
  { value: "netSales", label: "Net Sales" },
  { value: "netProceeds", label: "Net Proceeds" },
  { value: "netMargin", label: "Net Margin" },
];

export const DataEntry: React.FC = () => {
  const { formData } = useFormStore();
  const [search, setSearch] = useState<string>("");
  const [activeTab, setActiveTab] = useState<TabsType>("SKU");

  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDocUploaded, setIsDocUploaded] = useState<boolean>(false);
  const [isDataEntryModalOpen, setIsDataEntryModalOpen] =
    useState<boolean>(false);

  const tableRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);
  const [data, setData] = useState<any[]>(getData(activeTab));
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<any | null>(null);
  const [isUpdated, setIsUpdated] = useState<boolean>(false);

  const fetchMap: Record<string, () => Promise<any>> = {
    COGS: () => dataEntryApi.listCogsData({ offset: 0, limit: 50 }),
  };

  const triggerUpdate = () => setIsUpdated((prev) => !prev);

  useEffect(() => {
    if (activeTab === "COGS") {
      const fetchData = async () => {
        try {
          const result = await fetchMap["COGS"]();
          console.log(result);
          setData(result);
        } catch (err) {
          console.error("Failed to fetch COGS data", err);
        }
      };
      fetchData();
    } else {
      setData(getData(activeTab));
    }
  }, [activeTab, isUpdated]);

  useEffect(() => {
    if (tableRef.current) {
      const tableHeight = tableRef?.current?.offsetHeight;
      const headerHeight = activeTab === "SKU" ? 250 : 280;
      setHeight(tableHeight - headerHeight);
    }
  }, [activeTab, tableRef?.current?.offsetHeight]);

  const handleFileSelect = (files: File[]) => {
    setSelectedFiles(files);
  };

  const handleFormSubmit = async () => {
    const data = formData[activeTab];
    if (!data) return;

    if (activeTab === "SKU") {
      const {
        date = "",
        asin = "",
        cogs = "0",
        unitsSold = "0",
        priceValue = "0",
        unitsReturned1Value = "0",
      } = data;

      const today = new Date().toISOString().split("T")[0];
      const date_start = date;
      const date_end = today;

      const amazon_fees = buildGroup(data, "amazonFee", 3, amazonFeeOptions);
      const additional_metrics = buildGroup(
        data,
        "unitsReturned",
        2,
        unitsReturnedOptions,
      );

      const payload = [
        {
          asin,
          date_start,
          date_end,
          price: Number(priceValue),
          cogs: Number(cogs),
          units_sold_items: Number(unitsSold),
          units_returned_items: Number(unitsReturned1Value),
          ...(Object.keys(additional_metrics).length && { additional_metrics }),
          ...(Object.keys(amazon_fees).length && { amazon_fees }),
        },
      ];

      await new CategoriesService().sendCogsData(payload);
    }
    triggerUpdate();
    setIsDataEntryModalOpen(false);
  };

  const openEditCogModal = (record: any) => {
    const newRecord: COGSFormData = {
      product: record.product_id,
      currency: record.currency,
      cogs: record.cogs,
      miscellaneous: record.misc,
      total: record.fees,
      startDate: record.date_start,
      endDate: record.date_end,
    };

    setEditRecord(newRecord);
    setEditModalOpen(true);
  };

  const toDateString = (input: string) => {
    return input ? input.slice(0, 10) : "";
  };

  const handleEditCogSuccess = async (values: any) => {
    try {
      if (!editRecord) return;

      const query: CogsUpdateQueryParams = {
        product_id: editRecord.product,
        date_start: toDateString(editRecord.startDate),
        date_end: toDateString(editRecord.endDate),
      };

      const body: CogsUpdateBody = {
        currency: values.currency,
        cogs: Number(values.cogs),
        fees: Number(values.fees),
        misc: Number(values.misc),
      };

      await dataEntryApi.updateCogsData(query, body);
      setEditModalOpen(false);
      triggerUpdate();
    } catch (err) {
      console.error("Failed to update COGS:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unknown error occurred";

      toast.error(`Failed to update COGS: ${message}`);
    }
  };

  const handleDeleteCogs = async (record: any) => {
    try {
      const params: CogsDeleteQueryParams = {
        product_id: record.product_id,
        date_start: toDateString(record.date_start),
        date_end: toDateString(record.date_end),
        product_source: "AMAZON",
      };

      await dataEntryApi.deleteCogsData(params);
      triggerUpdate();
    } catch (error) {
      console.error("Failed to delete COGS entry:", error);
    }
  };

  const columns = getColumns(activeTab, {
    openEditModal: openEditCogModal,
    handleDeleteCogs: handleDeleteCogs,
  });

  const disableApply =
    activeTab === "SKU"
      ? !(
          formData.SKU?.date &&
          formData.SKU?.asin &&
          formData.SKU?.cogs &&
          formData.SKU?.priceValue
        )
      : !formData[activeTab] ||
        Object.values(formData[activeTab] || {}).some((v) => !v);

  return (
    <div
      className={classNames(
        styles.container,
        styles[activeTab.toLowerCase().replace(/ /g, "_")],
        { [styles.uploaded]: isDocUploaded },
        { [styles.not_uploaded]: !isDocUploaded },
      )}
      ref={tableRef}
      id={styles.container}
    >
      <h2>Data Entry</h2>
      <Tabs
        activeTab={activeTab}
        setActiveTab={(tab) => setActiveTab(tab)}
        tabs={tabs}
      />
      <div className={styles.actions}>
        <Input
          placeholder="Search elements"
          value={search}
          onChange={setSearch}
          icon={<Search />}
        />
        <div className={styles.buttons}>
          {activeTab === "COGS" && (
            <button
              className={styles.upload}
              onClick={() => setIsDataEntryModalOpen(true)}
            >
              Add COGS
            </button>
          )}
          <button
            className={styles.upload}
            onClick={() => setIsUploadOpen((o) => !o)}
          >
            <Upload color="#5295E0" size={16} /> Upload CSV
          </button>
          <button
            className={styles.add}
            onClick={() => setIsDataEntryModalOpen(true)}
          >
            Add data entry
          </button>
        </div>
      </div>

      <ExpandableTable
        columns={columns}
        data={data}
        scroll={{ x: "max-content", y: `${height}px` }}
      />

      <ModalBox
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload"
        className={styles.modal}
      >
        <div className={styles.uploadContainer}>
          <FileUploadDropzone onFileSelect={handleFileSelect} />
        </div>
        {selectedFiles.length > 0 && (
          <div className={styles.uploadingFiles}>
            <p>
              Uploading - {selectedFiles.length}/{selectedFiles.length} file
            </p>
            {selectedFiles.map((file, index) => (
              <div key={index} className={styles.fileItem}>
                <span className={styles.fileName}>{file.name}</span>
                <button
                  className={styles.delete}
                  onClick={() => {
                    const updatedFiles = selectedFiles.filter(
                      (_, i) => i !== index,
                    );
                    setSelectedFiles(updatedFiles);
                  }}
                >
                  <X size={20} color="#5C5C5A" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className={styles.actionButtons}>
          <button
            className={styles.cancelButton}
            onClick={() => {
              setSelectedFiles([]);
              setIsUploadOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            disabled={selectedFiles.length === 0}
            className={styles.uploadButton}
            onClick={() => {
              setIsDocUploaded(true);
              setIsUploadOpen(false);
            }}
          >
            Upload Files
          </button>
        </div>
      </ModalBox>

      <ModalBox
        isOpen={isDataEntryModalOpen}
        onClose={() => setIsDataEntryModalOpen(false)}
        title={getTitle(activeTab)}
        className={styles.modal}
      >
        {getFormComponent(activeTab, activeTab, handleFormSubmit)}
        {activeTab !== "COGS" && (
          <div className={styles.actionButtons}>
            <button
              className={styles.uploadButton}
              disabled={disableApply}
              onClick={handleFormSubmit}
            >
              Add Data Entry
            </button>
          </div>
        )}
      </ModalBox>

      {editModalOpen && (
        <ModalBox
          className={styles.modal}
          title="Edit COGS Data Entry"
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
        >
          <COGSForm
            className={styles.cogsForm}
            initialData={editRecord}
            onEditCogSuccess={handleEditCogSuccess}
          />
        </ModalBox>
      )}
    </div>
  );
};
export { Tabs };
